import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import apontamentoService from '../../services/apontamento.service';
import CalendarioCapacidade from '../../components/CalendarioCapacidade';

// ════════════════════════════════════════════════════════════════════════════
// CalendarioSelecaoOverlay — overlay tela cheia que abre o CalendarioCapacidade
// em modo seleção pra apontar horas em uma tarefa.
// ────────────────────────────────────────────────────────────────────────────
// Props:
//   - aberto: boolean
//   - tarefaId, tarefaTitulo: pra exibir no header
//   - onFechar: callback (cancelar)
//   - onSelecionar({ start, end }): callback ao desenhar intervalo no calendário
//
// O componente:
//   1. Resolve o colaborador do usuário logado (uma vez)
//   2. Mostra o CalendarioCapacidade com colaboradorId resolvido + modoSelecao=true
//   3. Ao arrastar e soltar no calendário, dispara onSelecionar
// ════════════════════════════════════════════════════════════════════════════

const COR = {
  azul:    '#003399', azulBg:  '#eff6ff',
  borda:   '#e8eaed', texto:   '#1a1a2e', muted: '#888',
  branco:  '#fff',    cinzaBg: '#fafafa',
  vermelho:'#dc2626', vermBg:  '#fff0f0',
  verde:   '#10b981',
};

function CalendarioSelecaoOverlay({
  aberto, tarefaId, tarefaTitulo, onFechar, onSelecionar,
  // r32 item 6: quando passado, usa esse colaborador em vez de resolver do usuario logado
  colaboradorOverride,
  headerTitulo,        // sobrescreve "Modo seleção — Apontar horas" se passado
  headerDescricao,     // sobrescreve a instrução abaixo do título
}) {
  const { usuario } = useAuth();
  const [colaborador, setColaborador]  = useState(null);
  const [carregando, setCarregando]    = useState(false);
  const [erro, setErro]                = useState('');

  // ── Resolve colaborador: prioridade pro override, senão usa o do usuario logado
  useEffect(() => {
    if (!aberto) return;
    setErro('');

    // Caso 1: override explícito — usa direto
    if (colaboradorOverride) {
      setColaborador(colaboradorOverride);
      setCarregando(false);
      return;
    }

    // Caso 2: resolve colaborador do usuario logado
    if (!usuario?.id) return;
    setColaborador(null);
    setCarregando(true);
    apontamentoService.meuColaborador(usuario.id)
      .then(c => setColaborador(c))
      .catch(err => {
        setErro(err.response?.data?.mensagem
          || 'Não foi possível resolver seu colaborador. Apontamento manual ainda funciona.');
      })
      .finally(() => setCarregando(false));
  }, [aberto, usuario, colaboradorOverride]);

  // ESC fecha
  useEffect(() => {
    if (!aberto) return;
    const handler = (e) => { if (e.key === 'Escape') onFechar(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [aberto, onFechar]);

  if (!aberto) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9000,
      backgroundColor: COR.cinzaBg,
      display: 'flex', flexDirection: 'column',
      fontFamily: "'Manrope', sans-serif",
    }}>
      {/* Header destacado */}
      <div style={{
        backgroundColor: COR.azul, color: COR.branco,
        padding: '14px 20px',
        display: 'flex', alignItems: 'center', gap: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}>
        <span style={{ fontSize: '20px' }}>📅</span>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: '14px', fontWeight: '700',
            fontFamily: 'Calibri, "Segoe UI", Arial, sans-serif',
            textTransform: 'uppercase', letterSpacing: '0.5px',
          }}>
            {headerTitulo || 'Modo seleção — Apontar horas'}
          </div>
          <div style={{ fontSize: '12px', opacity: 0.9, marginTop: '2px' }}>
            {headerDescricao || (
              <>
                Clique e arraste no calendário pra selecionar o intervalo em
                <strong> #{tarefaId} {tarefaTitulo}</strong>
              </>
            )}
          </div>
        </div>
        <button onClick={onFechar} style={{
          padding: '8px 18px', fontSize: '12px', fontWeight: '700',
          border: `1px solid ${COR.branco}`, borderRadius: '5px',
          backgroundColor: 'transparent', color: COR.branco, cursor: 'pointer',
          fontFamily: "'Manrope', sans-serif",
        }}>×  Cancelar</button>
      </div>

      {/* Corpo */}
      <div style={{ flex: 1, overflow: 'hidden', padding: '12px', position: 'relative' }}>
        {carregando ? (
          <div style={{
            textAlign: 'center', padding: '60px 20px',
            color: COR.muted, fontSize: '13px',
          }}>
            Carregando seu calendário...
          </div>
        ) : erro ? (
          <div style={{
            margin: '24px auto', maxWidth: '500px',
            padding: '16px 20px', backgroundColor: COR.vermBg,
            border: `1px solid ${COR.vermelho}`, borderRadius: '6px',
            color: COR.vermelho, fontSize: '13px',
          }}>
            <strong>⚠ Não foi possível abrir o calendário.</strong>
            <div style={{ marginTop: '6px' }}>{erro}</div>
            <div style={{ marginTop: '12px', textAlign: 'right' }}>
              <button onClick={onFechar} style={{
                padding: '6px 14px', fontSize: '12px', fontWeight: '600',
                border: `1px solid ${COR.vermelho}`, borderRadius: '5px',
                backgroundColor: COR.branco, color: COR.vermelho, cursor: 'pointer',
              }}>Fechar</button>
            </div>
          </div>
        ) : colaborador ? (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Dica de uso (banner) */}
            <div style={{
              padding: '8px 14px', marginBottom: '8px',
              backgroundColor: COR.azulBg, border: `1px solid ${COR.azul}33`,
              borderRadius: '5px', fontSize: '12px', color: COR.texto,
              display: 'flex', alignItems: 'center', gap: '8px',
            }}>
              <span style={{ fontSize: '16px' }}>👆</span>
              <span>
                <strong>Clique e arraste</strong> em um horário vazio do calendário
                pra criar o apontamento. Após soltar, você confirma os detalhes.
              </span>
            </div>

            <div style={{ flex: 1, minHeight: 0 }}>
              <CalendarioCapacidade
                colaboradorId={colaborador.id}
                modoSelecao
                onSelecionarIntervalo={({ start, end }) => {
                  // Dispara callback e fecha overlay
                  onSelecionar({ start, end });
                }}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default CalendarioSelecaoOverlay;
